// import { useMsal } from '@azure/msal-react';
/* eslint-disable */
import { useQuery } from "@tanstack/react-query";
import { AutoComplete, Card, Flex } from "antd";
import { useNavigate, useParams } from "react-router";
import { Outlet } from "react-router-dom";
import { toast } from "react-toastify";

import styles from "./DestinationSelector.module.scss";

import { getDestinationMetadata } from "../../api/template/template";
import { useUserData } from "../../hooks/useUserProfile";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { clearDataverseState, setDestinationIdentifier } from '../../store/slices/dataverseSlice';
import { createErrorMessage } from "../../utils/createErrorMessage";
import { ErrorView } from "../Error/ErrorView";
import { useEffect, useState } from "react";
import { ImportConfigurationsDrawer } from "./ImportConfigurationsDrawer";
import { CustomButton } from "../FormFields/CustomButton";

interface SourceEntitySchema {
    identifier: string;
    displayName: string;
    properties: Array<{
        identifier: string;
        displayName: string;
    }>;
}

export const DestinationSelector = () => {
    const urlParams = useParams();
    const navigate = useNavigate();
    const { tenantId } = useUserData();
    const dispatch = useAppDispatch();
    const [configurationsDrawerOpen, setConfigurationsDrawerOpen] = useState(false);
    const { destinationIdentifier, selectedInstance, selectedEntity } = useAppSelector(state => state.dataverse);

    useEffect(() => {
        if (urlParams.destinationIdentifier) {
            dispatch(setDestinationIdentifier(urlParams.destinationIdentifier));
        }
    }, [urlParams.destinationIdentifier]);

    const { data: destinationMetadataData, isLoading, isError, error } = useQuery({
        queryKey: ["getDestinationMetadata"],
        queryFn: async () => {
        try {
            const res = await getDestinationMetadata();
            return res.data.data;
        } catch (error) {
            toast.error(createErrorMessage(error));
        }
        },
        enabled: !!tenantId
    });

    if (isLoading && !urlParams.destinationIdentifier) return <p>Loading...</p>;

    if (isError) return <ErrorView error={error} showDetails />;

    const destinationItems = destinationMetadataData?.map(
        (property: SourceEntitySchema) => ({
            value: property.identifier,
            label: property.displayName || property.identifier
        })
    );
    
    return (
        <Flex
            vertical
            style={{ width: "100%", height: "100%" }}
            className={styles.destinationSelector}
        >
            {destinationIdentifier && (
                <Flex vertical style={{ width: "100%" }}>
                    <Flex justify="space-between" align="center" style={{ marginBottom: "8px" }}>
                        <Flex align="center" gap={8}>
                            <h3>Importing</h3>
                            <AutoComplete
                                popupMatchSelectWidth
                                allowClear
                                variant="outlined"
                                value={destinationIdentifier}
                                options={destinationItems}
                                placeholder="Select a report type"
                                filterOption={(inputValue, option) => {
                                    const regex = new RegExp(inputValue, "i");
                                    return regex.test(String(option?.value ?? ""));
                                }}
                                onSelect={(value) => {
                                    navigate(`/dataverse/${value}`);
                                    dispatch(setDestinationIdentifier(value));
                                }}
                                onClear={() => {
                                    dispatch(setDestinationIdentifier(""));
                                }}
                                style={{ width: "150px" }}
                            />
                            {selectedInstance && <h3>from {selectedInstance} environment</h3>}
                            {selectedEntity && <h3>using {selectedEntity} table</h3>}
                        </Flex>
                        <Flex gap={8}>
                            <CustomButton
                                onClick={() => dispatch(clearDataverseState())}
                            >
                                Reset reducer
                            </CustomButton>
                            <CustomButton onClick={() => setConfigurationsDrawerOpen(true)}>
                                Open Configurations
                            </CustomButton>
                        </Flex>
                    </Flex>
                    {/* <Flex vertical style={{ width: "100%", height: "100%" }}> */}
                        <Outlet />
                    {/* </Flex> */}
                </Flex>
            )}
            {!destinationIdentifier && (
                <Flex style={{ width: "100%" }}>
                    {destinationMetadataData?.map((item: SourceEntitySchema) => {
                        return (
                            <Card
                                key={item.identifier}
                                style={{ width: "100%", margin: "0 8px" }}
                                hoverable
                                className="destination-card"
                                onClick={() => {
                                    navigate(`/dataverse/${item.identifier}`);
                                    dispatch(setDestinationIdentifier(item.identifier));
                                }}
                            >
                                <Flex vertical>
                                    <h3>{item.displayName || item.identifier}</h3>
                                    {/* <p>{item.description}</p> */}
                                </Flex>
                            </Card>
                        );
                    })}
                </Flex>
            )}
            <ImportConfigurationsDrawer
                isOpen={configurationsDrawerOpen}
                onClose={() => setConfigurationsDrawerOpen(false)}
            />
        </Flex>
    )
}